import { Link } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Brand";

const PLANS = [
  { id: "yearly", name: "Yearly", price: "$249.50", unit: "/ year", note: "About $20.79/month · 2 months free", badge: "Best value" },
  { id: "monthly", name: "Monthly", price: "$24.95", unit: "/ month", note: "Billed monthly · cancel anytime" },
];
const FEATURES = [
  "Unlimited guided L.O.V.E. pigment consultations for brows and lips",
  "Verified pigment library with CI numbers and sources",
  "Kit-first suggestions from the pigments you own",
  "Label scanning, bottle photos and your shelf-style kit",
  "Consultation reports for your records",
];
const FAQ = [
  ["How does the free trial work?", "You get 7 days of full access. A card is required to start, and you won't be charged until your trial ends. Cancel before then and you pay nothing."],
  ["Can I switch or cancel later?", "Yes. Manage or cancel your membership anytime from the Membership page in the app. Cancelling keeps your access until the end of the period you've paid for."],
  ["Is the price the same everywhere?", "Prices are in US dollars. Sales tax or VAT may be added at checkout depending on where you live."],
];

function PlanCard({ p, cta }) {
  return (
    <div className={`relative flex flex-col rounded-3xl border-2 bg-white/70 p-8 ${p.badge ? "border-[#C5A059] shadow-[0_24px_60px_rgba(44,24,16,0.08)]" : "border-[#EFE6DA]"}`} data-testid={`pricing-plan-${p.id}`}>
      {p.badge && <span className="absolute -top-3 left-8 rounded-full bg-[#2C1810] px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#C5A059]">{p.badge}</span>}
      <p className="text-sm font-semibold text-[#2C1810]">{p.name}</p>
      <p className="mt-3 flex items-baseline gap-1 text-[#2C1810]"><span className="font-serif text-5xl font-semibold" data-testid={`pricing-price-${p.id}`}>{p.price}</span><span className="text-sm text-[#6E5549]">{p.unit}</span></p>
      <p className="mt-1 text-sm text-[#6E5549]">{p.note}</p>
      <p className="mt-4 rounded-xl bg-[#F6F1EA] px-4 py-2.5 text-sm text-[#4A3B32]">7 days free, then {p.price}{p.unit.replace("/ ", "/")}</p>
      <Link to={cta.to} onClick={() => localStorage.setItem("ig-plan", p.id)} data-testid={`pricing-cta-${p.id}`}
        className={`mt-6 inline-flex h-12 items-center justify-center rounded-full text-sm font-semibold transition-colors ${p.badge ? "bg-[#C5A059] text-[#2C1810] hover:bg-[#d4b273]" : "bg-[#2C1810] text-[#FDFBF7] hover:bg-[#3D2317]"}`}>
        {cta.label}
      </Link>
    </div>
  );
}

export default function PricingPage() {
  const { user } = useAuth();
  const hasAccess = user && (user.role === "admin" || user.billing?.has_access);
  const cta = !user ? { to: "/signup", label: "Start 7-day free trial" } : hasAccess ? { to: "/billing", label: "View my membership" } : { to: "/subscribe", label: "Start 7-day free trial" };
  return (
    <div className="ig-grain min-h-screen bg-[#FDFBF7] px-6 py-8 sm:px-10" data-testid="pricing-page">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Link to="/welcome" aria-label="Ink Genius home"><Wordmark /></Link>
          <Link to={hasAccess ? "/" : "/welcome"} data-testid="pricing-back-btn" className="inline-flex min-h-[40px] items-center gap-2 rounded-full border border-[#E6DBCF] bg-white/70 px-4 text-sm font-medium text-[#2C1810] hover:border-[#C5A059]">
            <ArrowLeft size={15} /> {hasAccess ? "Back to the app" : "Back to home"}
          </Link>
        </div>
        <div className="ig-rise mt-12 max-w-2xl">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#9C6218]">Plans & pricing</p>
          <h1 className="mt-3 font-serif text-4xl font-semibold leading-tight text-[#2C1810] sm:text-5xl lg:text-6xl" data-testid="pricing-title">One membership. Every pigment decision.</h1>
          <p className="mt-4 text-base text-[#6E5549]">Both plans include everything in Ink Genius and start with a 7-day free trial.</p>
        </div>
        <div className="ig-rise ig-d1 mt-10 grid gap-6 md:grid-cols-2">{PLANS.map((p) => <PlanCard key={p.id} p={p} cta={cta} />)}</div>
        <div className="ig-rise ig-d2 mt-12 grid gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-serif text-2xl font-semibold text-[#2C1810]">Everything included</h2>
            <ul className="mt-5 space-y-3" data-testid="pricing-features">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-[#4A3B32]"><span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#C5A059]/20 text-[#9C6218]"><Check size={13} strokeWidth={2.5} /></span>{f}</li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-serif text-2xl font-semibold text-[#2C1810]">Questions</h2>
            <dl className="mt-5 space-y-5" data-testid="pricing-faq">
              {FAQ.map(([q, a]) => <div key={q}><dt className="text-sm font-semibold text-[#2C1810]">{q}</dt><dd className="mt-1 text-sm leading-relaxed text-[#6E5549]">{a}</dd></div>)}
            </dl>
          </div>
        </div>
        <p className="mt-14 border-t border-[#EFE6DA] pt-6 text-xs text-[#A88B7D]">For trained permanent makeup professionals. Secure checkout by Stripe.</p>
      </div>
    </div>
  );
}
